import { msg } from "@omegagrid/localize";
import { NodeSource, TreeAdapter, TreeOptions, TreeSource } from "@omegagrid/tree";
import { IndicatorDefinition, ModuleDefinition } from "@reporting/rp-types/definition";
import { getIndicatorKey } from "../common";
import { ModuleCode } from "@reporting/rp-types/accounting";

type SectionType = 'transaction' | 'balance' | 'var' | 'computed';

const sectionNames: Record<SectionType, string> = {
	transaction: msg('Transaction Indicators'),
	balance: msg('Balance Indicators'),
	var: msg('Variance Indicators'),
	computed: msg('Computed Indicators')
}

function getSectionByIndicator(module: ModuleCode, indicator: IndicatorDefinition) : SectionType {
	if (indicator.type == 'VAR') return 'var';
	if (indicator.type == 'COMP') return 'computed';
	if (['F','A'].includes(module)) return null;
	if (indicator.balance) return 'balance';
	return 'transaction';
}

export class IndicatorAdapter extends TreeAdapter {

	private indicators: Map<string, IndicatorDefinition> = null;
	private items: NodeSource[];

	constructor(
		private module: ModuleDefinition,
		private options?: Partial<TreeOptions>
	) { super() }

	async getIndicators(): Promise<Map<string, IndicatorDefinition>> {
		if (this.indicators) return this.indicators;
		this.indicators = new Map();
		this.module.indicators.filter(indicator => indicator.visible).forEach(indicator => {
			this.indicators.set(getIndicatorKey(indicator), indicator);
		});
		return this.indicators;
	}

	async getTree(): Promise<TreeSource> {
		if (this.items) return this.items;

		const indicators = await this.getIndicators();
		this.items = [];

		let section: SectionType, lastSection: SectionType;
		indicators.forEach(indicator => {
			section = getSectionByIndicator(this.module.module as ModuleCode, indicator);

			if (section != lastSection) {
				this.items.push({
					k: section,
					v: msg(sectionNames[section]),
					l: 0,
					ch: false,
					collapsed: true,
				});
			}

			this.items.push({
				k: `${indicator.type}_${indicator.id_indicator}`,
				v: msg(indicator.description),
				l: 1,
				props: {indicator}
			});

			lastSection = section;
		});

		this.items.push({
			k: "-1",
			v: msg('Custom Indicator'),
			l: 0,
			props: {indicator: {
				type: 'CUST',
				visible: true
			}}
		});

		return this.items;
	}

	getOptions(): Partial<TreeOptions> {
		return this.options || {checkboxes: false, selectMode: 'none', search: false};
	}

}
